// src/components/AdminSidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Home,
  Wrench,
  MessageSquare,
  Mail,
  Users,
  User,
  UserPlus,
  Settings,
  Folder,
  FolderOpen,
  TrendingUp,
  Wallet,
  Shield,
  FileText,
  Layers,
  BarChart3,
  CalendarOff,
  CalendarDays,
  Calendar,
  Lock,
  Star,
  Clock,
  Briefcase,
  UserCheck,
  CheckCircle2,
  Send,
  Building,
  Building2,
  Ruler,
  Landmark,
  ShoppingCart,
  ClipboardList,
  Search,
  Phone,
  Megaphone,
  Sparkles,
  Banknote,
  CreditCard,
  Receipt,
  Laptop,
  Monitor,
  Download,
  PenLine,
  Circle,
} from "lucide-react";

const Icon = ({ name, size = 20, stroke = "currentColor" }) => {
  const P = {
    menu: "M3 6h18M3 12h18M3 18h18",
    home: "M3 11.5 12 4l9 7.5v7.5H15v-6H9v6H3z",
    chat: "M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z",
    check: "M20 6 9 17l-5-5",
    users:
      "M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2m14-11a4 4 0 1 0-8 0 4 4 0 0 0 8 0z",
    folder: "M3 7h6l2 2h10v10H3z",
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={P[name] || P.menu} />
    </svg>
  );
};

/* ============================================================
   ICON MAP - emoji used in the menu  ->  outline icon
   (change an icon here and it changes everywhere)
============================================================ */
const ICON_MAP = {
  "🏠": Home,
  "🛠": Wrench,
  "💬": MessageSquare,
  "📧": Mail,
  "👥": Users,
  "👤": User,
  "🧑‍💼": UserPlus,
  "⚙": Settings,
  "📁": Folder,
  "📂": FolderOpen,
  "🗂": Layers,
  "📈": TrendingUp,
  "💰": Wallet,
  "💵": Banknote,
  "💳": CreditCard,
  "🧾": Receipt,
  "🛡": Shield,
  "📝": PenLine,
  "📄": FileText,
  "📑": ClipboardList,
  "📋": ClipboardList,
  "📊": BarChart3,
  "🏖": CalendarOff,
  "🗓": CalendarDays,
  "📅": Calendar,
  "🔐": Lock,
  "⭐": Star,
  "🕒": Clock,
  "💼": Briefcase,
  "🤝": UserCheck,
  "✅": CheckCircle2,
  "📤": Send,
  "📩": Mail,
  "🏗": Building2,
  "🏢": Building,
  "🏛": Landmark,
  "🏦": Landmark,
  "📐": Ruler,
  "🛒": ShoppingCart,
  "🔍": Search,
  "📞": Phone,
  "📣": Megaphone,
  "✨": Sparkles,
  "💻": Laptop,
  "🖥": Monitor,
  "📥": Download,
};

const ICON_COLOR = "#c4c9e8"; // grey-lavender, readable on the dark sidebar

// emoji -> lucide component (variation selector removed so "🛠️" == "🛠")
const pickIcon = (emoji) =>
  ICON_MAP[String(emoji || "").replace(/\uFE0F/g, "")];

// Only the outline icon is shown (no coloured square).
// `bg` prop is still accepted so existing usages do not break, but it is ignored.
const ColorIcon = ({ icon }) => {
  const Cmp = pickIcon(icon) || Circle;

  return (
    <span
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: ICON_COLOR,
      }}
      aria-hidden
    >
      <Cmp size={20} strokeWidth={1.75} />
    </span>
  );
};

const SubIcon = ({ icon }) => {
  const Cmp = pickIcon(icon) || Circle;

  return (
    <span
      style={{
        width: 22,
        height: 22,
        minWidth: 22,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: ICON_COLOR,
      }}
      aria-hidden
    >
      <Cmp size={17} strokeWidth={1.75} />
    </span>
  );
};

// Chevron: single reusable expand/collapse indicator.
// Rotates from "collapsed" (pointing right) to "expanded" (pointing down)
// so every group/sub-group in the sidebar shares the exact same visual
// language for "this row has children".
//
// Deliberately a FILLED polygon (not a stroked path) with the color forced
// via an explicit `fill` prop rather than `currentColor`. Stroke-based
// icons can get silently zeroed out by a global CSS reset like
// `svg path { stroke: none }`, which some admin/dashboard templates ship
// with — a solid filled shape with an inline fill color survives that.
const Chevron = ({ open, size = 16, color = "#ffffff" }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      color,
      fontSize: size === 14 ? 10 : 11,
      fontWeight: 900,
      lineHeight: 1,
      transform: "none",
      visibility: "visible",
      opacity: 1,
    }}
  >
    {open ? "▲" : "▼"}
  </span>
);

const AdminSidebar = ({
  unreadCount = 0,
  breakpoint = 992,
  hideFab = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const mqQuery = `(max-width: ${breakpoint - 0.02}px)`;

  const getIsMobile = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.(mqQuery).matches || window.innerWidth < breakpoint);

  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [drawerOpen, setDrawerOpen] = useState(false);

  const open = () => setDrawerOpen(true);
  const close = () => setDrawerOpen(false);
  const toggle = () => setDrawerOpen((value) => !value);

  useEffect(() => {
    const mediaQuery = window.matchMedia(mqQuery);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener?.("change", handleChange);

    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, [mqQuery]);

  useEffect(() => {
    window.adminSidebarOpen = open;
    window.adminSidebarClose = close;
    window.adminSidebarToggle = toggle;

    return () => {
      delete window.adminSidebarOpen;
      delete window.adminSidebarClose;
      delete window.adminSidebarToggle;
    };
  }, []);

  useEffect(() => {
    const handleOpen = () => open();
    const handleClose = () => close();
    const handleToggle = () => toggle();

    window.addEventListener("sidebar:open", handleOpen);
    window.addEventListener("sidebar:close", handleClose);
    window.addEventListener("sidebar:toggle", handleToggle);

    document.addEventListener("sidebar:open", handleOpen);
    document.addEventListener("sidebar:close", handleClose);
    document.addEventListener("sidebar:toggle", handleToggle);

    return () => {
      window.removeEventListener("sidebar:open", handleOpen);
      window.removeEventListener("sidebar:close", handleClose);
      window.removeEventListener("sidebar:toggle", handleToggle);

      document.removeEventListener("sidebar:open", handleOpen);
      document.removeEventListener("sidebar:close", handleClose);
      document.removeEventListener("sidebar:toggle", handleToggle);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
      if (event.key?.toLowerCase?.() === "m") toggle();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const routeOpenState = (pathname) => ({
    tools: pathname.startsWith("/tools"),

    messenger: pathname.startsWith("/messenger"),
    email: pathname.startsWith("/email"),
    hr:
      pathname.startsWith("/attendance-page") ||
      pathname.startsWith("/admin/log") ||
      pathname.startsWith("/employee-drawing-report") ||
      pathname.startsWith("/leave-application") ||
      pathname.startsWith("/manage-employees") ||
      pathname.startsWith("/role-permissions") ||
      pathname.startsWith("/admin-employee-review-detail") ||
      pathname.startsWith("/attendance-page"),

    hiring:
      pathname.startsWith("/hr-dashboard") ||
      pathname.startsWith("/hr/job-openings") ||
      pathname.startsWith("/hr/candidates") ||
      pathname.startsWith("/hr/interviews") ||
      pathname.startsWith("/hr/offers") ||
      pathname.startsWith("/hr/joining") ||
      pathname.startsWith("/hr/closed-hiring") ||
      pathname.startsWith("/hr/recruitment-reports"),

    operations:
      pathname.startsWith("/transmittal") ||
      pathname.startsWith("/checklist") ||
      pathname.startsWith("/projects") ||
      pathname.startsWith("/drawing-master"),
    architect: pathname.startsWith("/construction-report"),

    // Project Management is its own top-level section (not nested under Operations)
    projectManagement:
      pathname.startsWith("/project-management") ||
      pathname.startsWith("/task-board") ||
      pathname.startsWith("/gantt-chart") ||
      pathname.startsWith("/calendar") ||
      pathname.startsWith("/drawings") ||
      pathname.startsWith("/boq-estimates") ||
      pathname.startsWith("/site-management") ||
      pathname.startsWith("/procurement") ||
      pathname.startsWith("/client-approval"),

    // CRM for USA LEADS
    crm:
      pathname.startsWith("/crm-dashboard") ||
      pathname.startsWith("/leads") ||
      pathname.startsWith("/proposals") ||
      pathname.startsWith("/sent-emails") ||
      pathname.startsWith("/wons-leads") ||
      pathname.startsWith("/lost-leads") ||
      pathname.startsWith("/reports"),
    sentEmails: pathname.startsWith("/sent-emails"),

    marketing:
      pathname === "/marketing/dashboard" || pathname.startsWith("/marketing/"),
    finance:
      pathname.startsWith("/finance") ||
      pathname.startsWith("/sales") ||
      pathname.startsWith("/quotation") ||
      pathname.startsWith("/proforma-invoice") ||
      pathname.startsWith("/invoice") ||
      pathname.startsWith("/finance-entry") ||
      pathname.startsWith("/company-expenses") ||
      pathname.startsWith("/project-cost-report") ||
      pathname.startsWith("/finance/reports/"),

    salesPurchase:
      pathname.startsWith("/finance-entry") ||
      pathname.startsWith("/finance/reports/gst/"),
    financeReports: pathname.startsWith("/finance/reports/"),
    gstReports: pathname.startsWith("/finance/reports/gst/"),
    tdsReports: pathname.startsWith("/finance/reports/tds/"),
    expense: pathname.startsWith("/company-expenses"),
    sales:
      pathname.startsWith("/sales") ||
      pathname.startsWith("/quotation") ||
      pathname.startsWith("/proforma-invoice") ||
      pathname.startsWith("/invoice"),

    masters:
      pathname.startsWith("/sales/services") ||
      pathname.startsWith("/sales/clients") ||
      pathname.startsWith("/sales/banks") ||
      pathname.startsWith("/sales/terms"),

    purchase: pathname.startsWith("/purchase"),
    purchaseMasters: pathname.startsWith("/purchase/vendors"),

    internalManagement:
      pathname.startsWith("/software") || pathname.startsWith("/hardware"),

    assets:
      pathname.startsWith("/software") || pathname.startsWith("/hardware"),
  });

  const [openGroup, setOpenGroup] = useState({
    tools: false,
    messenger: false,
    email: false,
    hr: false,
    hiring: false,
    operations: false,
    architect: false,
    projectManagement: false,
    crm: false,
    marketing: false,
    sentEmails: false,
    finance: false,
    sales: false,
    purchase: false,
    purchaseMasters: false,
    masters: false,
    salesPurchase: false,
    financeReports: false,
    gstReports: false,
    tdsReports: false,

    expense: false,
    internalManagement: false,
    assets: false,
  });

  useEffect(() => {
    const routeState = routeOpenState(location.pathname);

    setOpenGroup((previous) => {
      const next = { ...previous };

      Object.keys(routeState).forEach((key) => {
        if (routeState[key]) {
          next[key] = true;
        }
      });

      return next;
    });
  }, [location.pathname]);
  const toggleGroup = (key) => {
    setOpenGroup((previous) => {
      const nextValue = !previous[key];

      // Nested groups: parent group ko close kiye bina open/close honge.
      if (
        key === "salesPurchase" ||
        key === "financeReports" ||
        key === "gstReports" ||
        key === "tdsReports" ||
        key === "expense" ||
        key === "sales" ||
        key === "masters" ||
        key === "purchase" ||
        key === "purchaseMasters" ||
        key === "assets" ||
        key === "architect" ||
        key === "sentEmails" ||
        key === "marketing"
      ) {
        return {
          ...previous,
          [key]: nextValue,
        };
      }

      // Main sidebar groups me ek time par ek main group open rahega.
      return {
        tools: key === "tools" ? nextValue : false,
        messenger: key === "messenger" ? nextValue : false,
        email: key === "email" ? nextValue : false,
        hr: key === "hr" ? nextValue : false,

        hiring: key === "hiring" ? nextValue : false,

        operations: key === "operations" ? nextValue : false,

        architect:
          key === "operations" && nextValue ? previous.architect : false,

        projectManagement: key === "projectManagement" ? nextValue : false,

        crm: key === "crm" ? nextValue : false,

        sentEmails: key === "crm" && nextValue ? previous.sentEmails : false,
        sales: key === "finance" && nextValue ? previous.sales : false,

        masters: key === "finance" && nextValue ? previous.masters : false,
        purchase: key === "finance" && nextValue ? previous.purchase : false,

        purchaseMasters:
          key === "finance" && nextValue ? previous.purchaseMasters : false,
        finance: key === "finance" ? nextValue : false,

        salesPurchase:
          key === "finance" && nextValue ? previous.salesPurchase : false,
        financeReports:
          key === "finance" && nextValue ? previous.financeReports : false,
        gstReports:
          key === "finance" && nextValue ? previous.gstReports : false,
        tdsReports:
          key === "finance" && nextValue ? previous.tdsReports : false,
        expense: key === "finance" && nextValue ? previous.expense : false,

        internalManagement: key === "internalManagement" ? nextValue : false,

        assets:
          key === "internalManagement" && nextValue ? previous.assets : false,
      };
    });
  };
  const wrapperStyle = useMemo(() => {
    if (!isMobile) return styles.sidebarPinned;

    return {
      ...styles.sidebarDrawer,
      transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
    };
  }, [isMobile, drawerOpen]);

  const handleNavClick = () => {
    if (isMobile) close();
  };

  const getNavStyle = ({ isActive }) => ({
    ...styles.subItem,
    ...(isActive ? styles.subItemActive : {}),
  });

  const Menu = (
    <>
      <div
        style={styles.brand}
        onClick={() => {
          navigate("/admin");
          handleNavClick();
        }}
      >
        <div style={styles.logoDot} />
        <span style={styles.brandText}>Admin</span>
      </div>

      <nav>
        {/* Admin Dashboard */}
        <NavLink
          to="/admin"
          end
          onClick={handleNavClick}
          style={({ isActive }) => ({
            ...styles.item,
            ...(isActive ? styles.itemActive : {}),
          })}
        >
          <ColorIcon icon="🏠" bg="#dbeafe" />
          <span style={styles.itemLabel}>Admin Dashboard</span>
        </NavLink>

        {/* Tool */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("tools")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.tools ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="🛠️" bg="#fef3c7" />
            <span style={styles.groupTitleText}>Tool</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.tools} />
            </span>
          </button>

          {openGroup.tools && (
            <div style={styles.subList}>
              <NavLink
                to="/tools/notes"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📝" bg="#e0f2fe" />
                <span>Notes</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Messenger */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("messenger")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.messenger ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="💬" bg="#fce7f3" />
            <span style={styles.groupTitleText}>
              Messenger
              {unreadCount > 0 && (
                <span style={styles.badge}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.messenger} />
            </span>
          </button>

          {openGroup.messenger && (
            <div style={styles.subList}>
              <NavLink
                to="/messenger"
                end
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="👤" bg="#e0f2fe" />
                User Message
              </NavLink>

              <NavLink
                to="/messenger/project-communication"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📁" bg="#fef3c7" />
                Project-wise
              </NavLink>

              <NavLink
                to="/messenger/category-communication"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🗂️" bg="#ede9fe" />
                Category-wise
              </NavLink>
            </div>
          )}
        </div>
        {/* ================= EMAIL ================= */}
        {/* ================= EMAIL ================= */}
        <NavLink
          to="/email/inbox"
          onClick={handleNavClick}
          style={({ isActive }) => ({
            ...styles.item,
            ...(location.pathname.startsWith("/email")
              ? styles.itemActive
              : {}),
          })}
        >
          <ColorIcon icon="📧" bg="#dbeafe" />
          <span style={styles.itemLabel}>Email</span>
        </NavLink>
        {/* ================= END EMAIL ================= */}
        {/* HR */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("hr")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.hr ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="👥" bg="#ede9fe" />
            <span style={styles.groupTitleText}>HR</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.hr} />
            </span>
          </button>

          {openGroup.hr && (
            <div style={styles.subList}>
              <NavLink
                to="/hr/create-mail-account"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📧" bg="#dbeafe" />
                Create Mail Account
              </NavLink>

              <NavLink
                to="/admin/log-summary"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📄" bg="#e0f2fe" />
                Logs
              </NavLink>

              <NavLink
                to="/employee-drawing-report"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📊" bg="#dcfce7" />

                <span style={styles.employeeReportText}>
                  <span>Employee Working</span>
                  <span>Report</span>
                </span>
              </NavLink>

              <NavLink
                to="/leave-application"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🏖️" bg="#fce7f3" />
                Leave Management
              </NavLink>

              <NavLink
                to="/manage-employees"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="👥" bg="#ede9fe" />
                Manage Employee
              </NavLink>

              <NavLink
                to="/role-permissions"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🔐" bg="#fef3c7" />
                Role &amp; Permission
              </NavLink>

              <NavLink
                to="/admin-employee-review-detail"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="⭐" bg="#fef9c3" />
                Create Employee Review
              </NavLink>

              <NavLink
                to="/attendance-page"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🕒" bg="#dbeafe" />
                Employee Attendance
              </NavLink>
            </div>
          )}
        </div>
        {/* Hiring Management */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("hiring")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.hiring ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="🧑‍💼" bg="#dcfce7" />
            <span style={styles.groupTitleText}>Hiring Management</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.hiring} />
            </span>
          </button>

          {openGroup.hiring && (
            <div style={styles.subList}>
              <NavLink
                to="/hr-dashboard"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📊" bg="#dbeafe" />
                Hiring Dashboard
              </NavLink>

              <NavLink
                to="/hr/job-openings"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="💼" bg="#fef3c7" />
                Job Openings
              </NavLink>

              <NavLink
                to="/hr/candidates"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="👤" bg="#ede9fe" />
                Candidates
              </NavLink>

              <NavLink
                to="/hr/interviews"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🗓️" bg="#e0f2fe" />
                Interviews
              </NavLink>

              <NavLink
                to="/hr/offers"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📄" bg="#fce7f3" />
                Offers
              </NavLink>

              <NavLink
                to="/hr/joining"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🤝" bg="#dcfce7" />
                Upcoming Joinings
              </NavLink>

              <NavLink
                to="/hr/closed-hiring"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Closed Hiring
              </NavLink>

              <NavLink
                to="/hr/recruitment-reports"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📈" bg="#dbeafe" />
                Recruitment Reports
              </NavLink>
            </div>
          )}
        </div>
        {/* Operations */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("operations")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.operations ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="⚙️" bg="#fef3c7" />
            <span style={styles.groupTitleText}>Operations</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.operations} />
            </span>
          </button>

          {openGroup.operations && (
            <div style={styles.subList}>
              <NavLink
                to="/transmittal"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📤" bg="#e0f2fe" />
                Transmittal
              </NavLink>

              <NavLink
                to="/checklist"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Checklist
              </NavLink>

              <NavLink
                to="/projects/new"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🏗️" bg="#fef3c7" />
                Add Project
              </NavLink>

              <NavLink
                to="/drawing-master"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📐" bg="#ede9fe" />
                Drawing Master
              </NavLink>

              <button
                type="button"
                onClick={() => toggleGroup("architect")}
                style={styles.financeSubToggle}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="🏛️" bg="#e0f2fe" />
                  Architect
                </span>

                <Chevron open={openGroup.architect} size={14} />
              </button>

              {openGroup.architect && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/construction-report"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🏗️" bg="#dcfce7" />
                    Construction Report
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Project Management — its own top-level heading (not nested under Operations) */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("projectManagement")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.projectManagement ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="📁" bg="#fef3c7" />
            <span style={styles.groupTitleText}>Project Management</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.projectManagement} />
            </span>
          </button>

          {openGroup.projectManagement && (
            <div style={styles.subList}>
              <NavLink
                to="/project-management-dashboard"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📊" bg="#dbeafe" />
                Dashboard
              </NavLink>

              <NavLink
                to="/task-board"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🗂️" bg="#fce7f3" />
                Task Board
              </NavLink>

              <NavLink
                to="/gantt-chart"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📈" bg="#dbeafe" />
                Gantt Chart
              </NavLink>

              <NavLink
                to="/calendar"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📅" bg="#dcfce7" />
                Calendar
              </NavLink>

              <NavLink
                to="/drawings"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📐" bg="#ede9fe" />
                Drawings
              </NavLink>

              <NavLink
                to="/boq-estimates"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="💰" bg="#fef9c3" />
                BOQ & Estimates
              </NavLink>

              <NavLink
                to="/site-management"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🏗️" bg="#dcfce7" />
                Site Management
              </NavLink>

              <NavLink
                to="/procurement"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🛒" bg="#fce7f3" />
                Procurement
              </NavLink>

              <NavLink
                to="/client-approval"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Client Approval
              </NavLink>

              <NavLink
                to="/reports"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📑" bg="#dbeafe" />
                Reports
              </NavLink>
            </div>
          )}
        </div>
        {/* CRM */}
        {/* CRM */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("crm")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.crm ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="📈" bg="#e0f2fe" />
            <span style={styles.groupTitleText}>Business Development</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.crm} />
            </span>
          </button>

          {openGroup.crm && (
            <div style={styles.subList}>
              <NavLink
                to="/crm-dashboard"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📊" bg="#dbeafe" />
                CRM Dashboard
              </NavLink>

              <NavLink to="/leads" onClick={handleNavClick} style={getNavStyle}>
                <SubIcon icon="🗂️" bg="#dcfce7" />
                Leads
              </NavLink>

              <NavLink
                to="/proposals"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🔍" bg="#ede9fe" />
                Proposals
              </NavLink>

              <button
                type="button"
                onClick={() => {
                  toggleGroup("sentEmails");

                  // Sent Emails par click karte hi default Intro Mail page khulega
                  if (!location.pathname.startsWith("/sent-emails")) {
                    navigate("/sent-emails/intro");
                  }

                  handleNavClick();
                }}
                style={{
                  ...styles.financeSubToggle,

                  ...(location.pathname.startsWith("/sent-emails")
                    ? styles.subItemActive
                    : {}),
                }}
              >
                <span style={styles.financeSubTitle}>
                  <SubIcon icon="📧" bg="#fce7f3" />
                  Sent Emails
                </span>

                <Chevron open={openGroup.sentEmails} size={14} />
              </button>

              {openGroup.sentEmails && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/sent-emails/intro"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📩" bg="#dbeafe" />
                    Intro Mail
                  </NavLink>

                  <NavLink
                    to="/sent-emails/portfolio"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📁" bg="#dcfce7" />
                    Portfolio Mail
                  </NavLink>

                  <NavLink
                    to="/sent-emails/proposal"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📄" bg="#fef3c7" />
                    Proposal Mail
                  </NavLink>
                </div>
              )}

              <NavLink
                to="/wons-leads"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📞" bg="#fef3c7" />
                Wons Leads
              </NavLink>

              <NavLink
                to="/lost-leads"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="🤝" bg="#dcfce7" />
                Lost Leads
              </NavLink>

              <NavLink
                to="/reports"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📈" bg="#dbeafe" />
                Reports
              </NavLink>
              {/* ================= MARKETING ================= */}

              <button
                type="button"
                onClick={() => {
                  toggleGroup("marketing");

                  if (!location.pathname.startsWith("/marketing")) {
                    navigate("/marketing/dashboard");
                  }

                  handleNavClick();
                }}
                style={{
                  ...styles.financeSubToggle,
                  ...(location.pathname.startsWith("/marketing")
                    ? styles.subItemActive
                    : {}),
                }}
              >
                <span style={styles.financeSubTitle}>
                  <SubIcon icon="📣" bg="#ede9fe" />
                  Marketing
                </span>

                <Chevron open={openGroup.marketing} size={14} />
              </button>

              {openGroup.marketing && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/marketing/dashboard"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📊" bg="#dbeafe" />
                    AI Marketing Dashboard
                  </NavLink>

                  <NavLink
                    to="/marketing/create-post"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="✨" bg="#f3e8ff" />
                    Create Post
                  </NavLink>
                </div>
              )}

              {/* ================= END MARKETING ================= */}
            </div>
          )}
        </div>
        {/* Finance */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("finance")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.finance ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="💰" bg="#dcfce7" />
            <span style={styles.groupTitleText}>Finance</span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.finance} />
            </span>
          </button>

          {openGroup.finance && (
            <div style={styles.subList}>
              <NavLink
                to="/finance"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="📈" bg="#dcfce7" />
                Finance Dashboard
              </NavLink>
              <NavLink
                to="/project-cost-report"
                onClick={handleNavClick}
                style={getNavStyle}
              >
                <SubIcon icon="💵" bg="#dcfce7" />
                Project Cost
              </NavLink>
              {/* Sales & Purchase > Finance Entry / Report > GST */}
              <button
                type="button"
                onClick={() => toggleGroup("salesPurchase")}
                style={styles.financeSubToggle}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="📂" bg="#dbeafe" /> Book Keeping
                </span>
                <Chevron open={openGroup.salesPurchase} size={14} />
              </button>
              {openGroup.salesPurchase && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/finance-entry"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="💳" bg="#dbeafe" /> Finance Entry
                  </NavLink>
                  <button
                    type="button"
                    onClick={() => toggleGroup("financeReports")}
                    style={styles.financeSubToggle}
                  >
                    <span style={styles.financeSubTitle}>
                      <SubIcon icon="📊" bg="#dcfce7" /> Report
                    </span>
                    <Chevron open={openGroup.financeReports} size={14} />
                  </button>
                  {openGroup.financeReports && (
                    <div style={styles.masterNestedList}>
                      <button
                        type="button"
                        onClick={() => toggleGroup("gstReports")}
                        style={styles.financeSubToggle}
                      >
                        <span style={styles.financeSubTitle}>
                          <SubIcon icon="🧾" bg="#fef3c7" /> GST
                        </span>
                        <Chevron open={openGroup.gstReports} size={14} />
                      </button>
                      {openGroup.gstReports && (
                        <div style={styles.masterNestedList}>
                          <NavLink
                            to="/finance/reports/gst/r1-sales"
                            onClick={handleNavClick}
                            style={getNavStyle}
                          >
                            <SubIcon icon="📄" bg="#dcfce7" /> R1 Sales
                          </NavLink>
                          <NavLink
                            to="/finance/reports/gst/3b-purchase"
                            onClick={handleNavClick}
                            style={getNavStyle}
                          >
                            <SubIcon icon="📄" bg="#dbeafe" /> 3B Purchase
                          </NavLink>
                        </div>
                      )}
                      {/* ================= TDS REPORT FOLDER ================= */}
                      <button
                        type="button"
                        onClick={() => toggleGroup("tdsReports")}
                        style={styles.financeSubToggle}
                      >
                        <span style={styles.financeSubTitle}>
                          <SubIcon icon="📁" bg="#e0f2fe" />
                          TDS
                        </span>

                        <Chevron open={openGroup.tdsReports} size={14} />
                      </button>

                      {openGroup.tdsReports && (
                        <div style={styles.masterNestedList}>
                          <NavLink
                            to="/finance/reports/tds/receivable"
                            onClick={handleNavClick}
                            style={getNavStyle}
                          >
                            <SubIcon icon="📥" bg="#dcfce7" />
                            TDS Receivable
                          </NavLink>

                          <NavLink
                            to="/finance/reports/tds/payable"
                            onClick={handleNavClick}
                            style={getNavStyle}
                          >
                            <SubIcon icon="💳" bg="#fef3c7" />
                            TDS Payment
                          </NavLink>
                        </div>
                      )}
                      {/* ================= END TDS FOLDER ================= */}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => toggleGroup("sales")}
                style={styles.financeSubToggle}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="💼" bg="#e0f2fe" />
                  Sales
                </span>

                <Chevron open={openGroup.sales} size={14} />
              </button>

              {openGroup.sales && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/sales/dashboard"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📊" bg="#dbeafe" />
                    Sales Dashboard
                  </NavLink>

                  <button
                    type="button"
                    onClick={() => toggleGroup("masters")}
                    style={{
                      ...styles.financeSubToggle,
                      ...(openGroup.masters ? styles.nestedToggleOpen : {}),
                    }}
                  >
                    <span style={styles.financeSubTitle}>
                      <SubIcon icon="🗂️" bg="#ede9fe" />
                      Masters
                    </span>

                    <Chevron open={openGroup.masters} size={14} />
                  </button>

                  {openGroup.masters && (
                    <div style={styles.masterNestedList}>
                      <NavLink
                        to="/sales/services"
                        onClick={handleNavClick}
                        style={getNavStyle}
                      >
                        <SubIcon icon="🛠️" bg="#dcfce7" />
                        Service Master
                      </NavLink>

                      <NavLink
                        to="/sales/clients"
                        onClick={handleNavClick}
                        style={getNavStyle}
                      >
                        <SubIcon icon="👥" bg="#ede9fe" />
                        Client Master
                      </NavLink>

                      <NavLink
                        to="/sales/banks"
                        onClick={handleNavClick}
                        style={getNavStyle}
                      >
                        <SubIcon icon="🏦" bg="#fef3c7" />
                        Bank Detail Master
                      </NavLink>

                      <NavLink
                        to="/sales/terms"
                        onClick={handleNavClick}
                        style={getNavStyle}
                      >
                        <SubIcon icon="📋" bg="#fce7f3" />
                        Terms & Conditions
                      </NavLink>
                    </div>
                  )}

                  <NavLink
                    to="/sales/quotations"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📝" bg="#e0f2fe" />
                    Quotations
                  </NavLink>

                  <NavLink
                    to="/sales/proforma-invoices"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📄" bg="#fef9c3" />
                    Proforma Invoices
                  </NavLink>

                  <NavLink
                    to="/sales/invoices"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🧾" bg="#dcfce7" />
                    Invoices
                  </NavLink>
                </div>
              )}

              {/* ================= PURCHASE ================= */}

              <button
                type="button"
                onClick={() => toggleGroup("purchase")}
                style={{
                  ...styles.financeSubToggle,
                  ...(openGroup.purchase ? styles.nestedToggleOpen : {}),
                }}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="🛒" bg="#fef3c7" />
                  Purchase
                </span>

                <Chevron open={openGroup.purchase} size={14} />
              </button>

              {openGroup.purchase && (
                <div style={styles.financeNestedList}>
                  {/* Purchase Dashboard */}
                  <NavLink
                    to="/purchase/dashboard"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📊" bg="#dbeafe" />
                    Purchase Dashboard
                  </NavLink>

                  {/* Quotations */}
                  <NavLink
                    to="/purchase/quotations"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📝" bg="#e0f2fe" />
                    Quotations
                  </NavLink>
                  <NavLink
                    to="/purchase/proforma-invoices"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="📄" bg="#fef9c3" />
                    Proforma Invoices
                  </NavLink>

                  {/* Invoices */}
                  <NavLink
                    to="/purchase/invoices"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🧾" bg="#dcfce7" />
                    Invoices
                  </NavLink>

                  {/* ================= PURCHASE MASTERS ================= */}

                  <button
                    type="button"
                    onClick={() => toggleGroup("purchaseMasters")}
                    style={{
                      ...styles.financeSubToggle,
                      ...(openGroup.purchaseMasters
                        ? styles.nestedToggleOpen
                        : {}),
                    }}
                  >
                    <span style={styles.financeSubTitle}>
                      <SubIcon icon="🗂️" bg="#ede9fe" />
                      Masters
                    </span>

                    <Chevron open={openGroup.purchaseMasters} size={14} />
                  </button>

                  {openGroup.purchaseMasters && (
                    <div style={styles.masterNestedList}>
                      {/* Vendor Master */}
                      <NavLink
                        to="/purchase/vendors"
                        onClick={handleNavClick}
                        style={getNavStyle}
                      >
                        <SubIcon icon="🏢" bg="#ede9fe" />
                        Vendor Master
                      </NavLink>
                    </div>
                  )}
                </div>
              )}

              {/* ================= END PURCHASE ================= */}
              {/* <button
                type="button"
                onClick={() => toggleGroup("expense")}
                style={styles.financeSubToggle}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="📊" bg="#fef9c3" />
                  Company Expenses
                </span>
                <Chevron open={openGroup.expense} size={14} />
              </button> */}

              {openGroup.expense && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/company-expenses/svb"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🏢" bg="#ede9fe" />
                    SVB
                  </NavLink>

                  <NavLink
                    to="/company-expenses/sdc"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🏢" bg="#dbeafe" />
                    SDC
                  </NavLink>

                  <NavLink
                    to="/company-expenses/magnira"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🏢" bg="#fef3c7" />
                    Magnira
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Admin & Internal Management */}
        <div style={styles.group}>
          <button
            type="button"
            onClick={() => toggleGroup("internalManagement")}
            style={{
              ...styles.groupHeader,
              ...(openGroup.internalManagement ? styles.groupHeaderOpen : {}),
            }}
          >
            <ColorIcon icon="🛡️" bg="#e0f2fe" />
            <span style={styles.groupTitleText}>
              Admin &amp; Internal Management
            </span>
            <span style={styles.chevron}>
              <Chevron open={openGroup.internalManagement} />
            </span>
          </button>

          {openGroup.internalManagement && (
            <div style={styles.subList}>
              <button
                type="button"
                onClick={() => toggleGroup("assets")}
                style={styles.financeSubToggle}
              >
                <span style={styles.financeSubTitle}>
                  <ColorIcon icon="💻" bg="#e0f2fe" />
                  Assets
                </span>
                <Chevron open={openGroup.assets} size={14} />
              </button>

              {openGroup.assets && (
                <div style={styles.financeNestedList}>
                  <NavLink
                    to="/software"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="💻" bg="#e0f2fe" />
                    Software Master
                  </NavLink>

                  <NavLink
                    to="/hardware"
                    onClick={handleNavClick}
                    style={getNavStyle}
                  >
                    <SubIcon icon="🖥️" bg="#ede9fe" />
                    Hardware Master
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );

  const headerHasHamburger =
    typeof window !== "undefined" && window.__HEADER_HAS_HAMBURGER__;

  const showFab = !hideFab && isMobile && !headerHasHamburger;

  return (
    <>
      {!isMobile && <aside style={styles.sidebarPinned}>{Menu}</aside>}

      {isMobile &&
        createPortal(
          <>
            <div
              onClick={close}
              style={{
                ...styles.backdrop,
                opacity: drawerOpen ? 1 : 0,
                pointerEvents: drawerOpen ? "auto" : "none",
              }}
            />

            <aside
              style={wrapperStyle}
              onMouseDownCapture={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {Menu}
            </aside>

            {showFab && (
              <button
                type="button"
                aria-label="Open menu"
                onClick={open}
                style={styles.fab}
              >
                <Icon name="menu" size={22} />
              </button>
            )}
          </>,
          document.body,
        )}
    </>
  );
};

const base = {
  bg: "#2f3470",
  text: "#e9ecff",
};

const styles = {
  sidebarPinned: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    height: "100%",
    position: "relative",
    background: base.bg,
    color: base.text,
    padding: "18px 14px",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    zIndex: 20,
  },

  sidebarDrawer: {
    width: "88vw",
    maxWidth: 360,
    boxSizing: "border-box",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    background: base.bg,
    color: base.text,
    padding: "18px 14px",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    boxShadow: "6px 0 24px rgba(0,0,0,0.35)",
    zIndex: 2147483000,
    transition: "transform .30s ease",
    willChange: "transform",
  },

  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.28)",
    zIndex: 2147482999,
    transition: "opacity .30s ease",
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 12px",
    borderRadius: 12,
    marginBottom: 10,
    cursor: "pointer",
  },

  logoDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#76e0fe",
    boxShadow: "0 0 0 4px rgba(118,224,254,0.2)",
  },

  brandText: {
    fontWeight: 900,
    letterSpacing: 0.6,
    fontSize: 22,
  },

  itemLabel: {
    fontWeight: 800,
    fontSize: 16,
  },

  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 12px",
    borderRadius: 12,
    textDecoration: "none",
    color: base.text,
    marginTop: 8,
    boxSizing: "border-box",
  },

  itemActive: {
    background: "linear-gradient(90deg, #5865d8 0%, #444ca8 100%)",
    color: "#ffffff",
    boxShadow: "inset 4px 0 0 #76e0fe, 0 5px 14px rgba(8, 15, 70, 0.28)",
    border: "1px solid rgba(255,255,255,0.16)",
  },

  group: {
    marginTop: 8,
  },

  // Grid layout (instead of flex) guarantees the middle text column has a
  // hard width limit, so long labels like "Business Development" or
  // "Admin & Internal Management" reliably wrap onto a second line instead
  // of overflowing past the sidebar edge.
  groupHeader: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    color: "#cfd6ff",
    border: "none",
    cursor: "pointer",
    padding: "10px 10px",
    borderRadius: 12,
    display: "grid",
    gridTemplateColumns: "28px minmax(0, 1fr) 20px",
    columnGap: 10,
    alignItems: "center",
    minHeight: 52,
    fontFamily: "inherit",
    textAlign: "left",
  },

  groupHeaderOpen: {
    background: "linear-gradient(90deg, #5865d8 0%, #444ca8 100%)",
    color: "#ffffff",
    boxShadow: "inset 4px 0 0 #76e0fe, 0 5px 14px rgba(8, 15, 70, 0.28)",
    border: "1px solid rgba(255,255,255,0.14)",
  },

  groupTitleText: {
    display: "block",
    minWidth: 0,
    maxWidth: "100%",
    fontWeight: 800,
    fontSize: 15,
    lineHeight: "19px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    textAlign: "left",
  },

  subList: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginTop: 4,
    paddingLeft: 14,
    borderLeft: "2px solid rgba(255,255,255,0.12)",
    gap: 4,
  },

  subItem: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "9px 10px",
    borderRadius: 11,
    textDecoration: "none",
    color: "#dfe4ff",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 2,
    overflow: "hidden",
  },

  subItemActive: {
    background: "linear-gradient(90deg, #5c68de 0%, #4751b2 100%)",
    color: "#ffffff",
    boxShadow: "inset 4px 0 0 #76e0fe, 0 4px 12px rgba(7, 13, 60, 0.25)",
    border: "1px solid rgba(255,255,255,0.16)",
  },

  chevron: {
    width: 20,
    minWidth: 20,
    height: 20,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    justifySelf: "end",
    color: "#ffffff",
    visibility: "visible",
    opacity: 1,
    overflow: "visible",
  },

  badge: {
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    background: "#ff477e",
    color: "#fff",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },

  fab: {
    position: "fixed",
    top: 14,
    left: 14,
    width: 50,
    height: 50,
    borderRadius: 15,
    background: base.bg,
    color: "#fff",
    display: "grid",
    placeItems: "center",
    border: "1px solid rgba(255,255,255,0.25)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
    zIndex: 2147483647,
    cursor: "pointer",
  },
  financeSubToggle: {
    width: "100%",
    boxSizing: "border-box",
    background: "transparent",
    color: "#dfe4ff",
    border: "none",
    cursor: "pointer",
    padding: "9px 10px",
    borderRadius: 11,
    fontWeight: 700,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 14,
    minHeight: 45,
    fontFamily: "inherit",
  },

  financeSubTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
    textAlign: "left",
    fontSize: 14,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  financeNestedList: {
    display: "grid",
    gap: 4,
    paddingLeft: 18,
    marginBottom: 8,
  },

  masterNestedList: {
    display: "grid",
    gap: 4,
    paddingLeft: 18,
    marginBottom: 6,
    borderLeft: "1px solid rgba(255,255,255,0.10)",
    marginLeft: 10,
  },

  nestedToggleOpen: {
    background: "rgba(255,255,255,0.07)",
  },
  employeeReportText: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    whiteSpace: "normal",
    lineHeight: "18px",
    textAlign: "left",
    overflow: "hidden",
    fontSize: 14,
  },
};

export default AdminSidebar;
